import React, { useState } from 'react';
import { useAuth } from '../../../AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../../../api/client';
import { isErrorResponse } from '../../../types/ErrorResponse';

type TryFormProps = {
  loadingStatus: boolean;
  trialEnabled: boolean;
};

export const TryForm: React.FC<TryFormProps> = ({ loadingStatus, trialEnabled }) => {
  const [email, setEmail] = useState('');
  const [tryLicenseAccepted, setTryLicenseAccepted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [licenseHtml, setLicenseHtml] = useState<string>('');
  const [loadingLicense, setLoadingLicense] = useState(false);
  const { demoLogin } = useAuth();
  const navigate = useNavigate();

  const fetchLicenseAgreement = async () => {
    if (licenseHtml) {
      setShowLicenseModal(true);
      return;
    }
    setLoadingLicense(true);
    try {
      const response = await fetch(apiUrl('/api/auth/license'));
      if (response.ok) {
        const html = await response.text();
        setLicenseHtml(html);
        setShowLicenseModal(true);
      } else {
        setError(
          `Failed to load license agreement (Status: ${response.status}). Please try again.`
        );
      }
    } catch {
      setError('Failed to load license agreement. Please check your connection and try again.');
    } finally {
      setLoadingLicense(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!trialEnabled) {
      setError(
        'Thank you for your interest in Walk It With Me. New trial accounts are not currently allowed. Please check again later for availability.'
      );
      return;
    }
    if (!tryLicenseAccepted) {
      setError('You must accept the license agreement to start a trial.');
      return;
    }
    try {
      setSubmitting(true);
      await demoLogin(email || undefined);
      navigate('/chat');
    } catch (err: unknown) {
      if (isErrorResponse(err)) {
        setError(err.message);
      } else {
        setError('Demo login failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="login-form">
        <h2 className="h4 mb-3">Try Walk It With Me</h2>
        {loadingStatus ? (
          <div className="text-muted d-flex align-items-center" aria-live="polite">
            <span
              className="spinner-border spinner-border-sm me-2 text-primary"
              role="status"
              aria-hidden="true"
            ></span>
            Checking trial availability
          </div>
        ) : !trialEnabled ? (
          <div className="alert alert-info" role="alert">
            Thank you for your interest in Walk It With Me. New trial accounts are not currently
            allowed. Please check again later for availability.
          </div>
        ) : (
          <>
            <div className="mb-3">
              <label htmlFor="tryEmail" className="form-label">
                Email (optional)
              </label>
              <input
                id="tryEmail"
                type="email"
                className="form-control"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="tryLicenseCheckbox"
                checked={tryLicenseAccepted}
                onChange={(e) => setTryLicenseAccepted(e.target.checked)}
                required
              />
              <label className="form-check-label" htmlFor="tryLicenseCheckbox">
                I have read and agree to the Walk It With Me{' '}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  onClick={fetchLicenseAgreement}
                >
                  license agreement
                </button>
              </label>
            </div>
            {error && (
              <div className="alert alert-danger py-2" role="alert">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={submitting || !tryLicenseAccepted}
            >
              {submitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Initializing demo...
                </>
              ) : (
                'Try'
              )}
            </button>
          </>
        )}
      </form>

      {showLicenseModal && (
        <div
          className="modal show d-block modal-overlay-custom"
          tabIndex={-1}
          role="dialog"
          aria-labelledby="licenseModalTitle"
          aria-describedby="licenseModalBody"
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 id="licenseModalTitle" className="modal-title">
                  License Agreement
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowLicenseModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div id="licenseModalBody" className="modal-body">
                {loadingLicense ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading license agreement...</p>
                  </div>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: licenseHtml }} />
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowLicenseModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TryForm;
